/**
 * Extract a query string parameter from the URL.
 *
 * @param {String} name
 * @return {String|null}
 */
export const queryStringValue = (name, url = window.location.href) => {
  name = name.replace(/[[\]]/g, '\\$&');
  var regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
      results = regex.exec(url);
  if (!results) return null;
  if (!results[2]) return '';
  return decodeURIComponent(results[2].replace(/\+/g, ' '));
}

/**
 * @param {Array} a
 * @return {Array}
 *  A shuffled version of the array
 */
export const shuffle = (a) => {
  let clone = [...a];
  var j, x, i;
  for (i = clone.length - 1; i > 0; i--) {
    j = Math.floor(Math.random() * (i + 1));
    x = clone[i];
    clone[i] = clone[j];
    clone[j] = x;
  }
  return clone;
};

/**
 * @param {number} count
 * @return {Array.<number>}
 *  An array of numbers from 1 to {count}
 */
export const numberBank = (count = 43) => {
  return new Array(count)
    .fill(undefined)
    .map((item, index) => index + 1)
}

/**
 * @return {Array.<number>}
 */
export const drawRandomNumbers = () => {
  let numbers = numberBank();
  return shuffle(numbers).slice(0,5).sort();
}

/**
 *
 * @param {Array.<number>} nums
 *   The numbers that should be excluded from the set and considered for closeness.
 * @param {number} length
 *   The number of items to return.
 * @param {Array.<number>|null} bank
 *   The bank of numbers to choose from. Defaults to numberBank().
 * @param {number} probability
 *  The probability to return a number that is not within 4 of a member of nums.
 * @return {Array.<number>}
 *  An array of
 */
export const numbersCloseTo = (nums, length, bank, probability = .05) => {
  bank = bank || numberBank();
  bank = shuffle(bank);
  let drawnNumbers = [];

  // Remove the specific numbers from the set.
  let drawable = bank.filter(value => !nums.includes(value));

  // Use filter to iterate and push into drawnNumbers. Remove a set member if used.
  drawable.filter(value => {
    if (drawnNumbers.length == length) {
      return true;
    }

    if (Math.random() < probability) {
      drawnNumbers.push(value);
      return false;
    }

    const randomOptions = shuffle(nums);

    for (let i in randomOptions) {
      if (Math.abs(randomOptions[i] - value) < 4) {
        drawnNumbers.push(value);
        return false;
      }
    }

    return true;
  });

  // Pad out to length with random numbers
  if (drawnNumbers.length < length) {
    drawnNumbers = drawnNumbers.concat(drawnNumbers.slice(0, length - drawnNumbers.length))
  }

  return drawnNumbers;
}

/**
 * Create the app container.
 *
 * @param {Node} container
 * @param {object} config
 */
export const createContainer = (container) => {

  /**
   * Styles needed for the outer container.
   */
  const style = document.createElement('style');
  style.setAttribute('type', 'text/css');
  style.appendChild(document.createTextNode(`
    :root {
      --cash-5-height: 100%;
    }

    body.no-scroll {
      overflow: hidden;
    }

    #cash-5-app-container {
      position: fixed;
      z-index: 999;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      /* width: 100vw; */

      /** iOS - JS & CSS https://dev.to/maciejtrzcinski/100vh-problem-with-ios-safari-3ge9 */
      height: 100vh;
      height: var(--cash-5-height);

      /** iOS - CSS only, https://dev.to/maciejtrzcinski/100vh-problem-with-ios-safari-3ge9 */
      /* height: 100%; */
      /* height: -webkit-fill-available; */

      background: #333;
      overflow: hidden;
      opacity: 0.6;
    }

    #cash-5-app-container.cash-5-app-container--hide {
      transform: scale(0.6);
      opacity: 0;
      visibility: hidden;
      transition:
        transform 0.4s,
        opacity 0.4s;
    }

    #cash-5-app-container.cash-5-app-container--pending {
      transform: scale(1);
      transition:
        transform 0s,
        opacity 0.2s;
    }

    #cash-5-app-container.cash-5-app-container--show {
      transform: scale(1);
      opacity: 1;
      visibility: visible;
      transition:
        transform 0.2s,
        opacity 0.4s;
    }

    #cash-5-app-container__close {
      position: absolute;
      right: 2rem;
      bottom: 2rem;
      padding-right: 1.2em;
      padding-left: 1.2em;
      border-radius: 999px;
      background-color: #95368e;
      background-image: linear-gradient(315deg, transparent 0.5em, #95368e 0);
    }

    .cash-5-app-loader {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      border: 16px solid #f3f3f3; /* Light grey */
      border-top: 16px solid #3498db; /* Blue */
      border-radius: 50%;
      width: 120px;
      height: 120px;
      animation: spin 2s linear infinite;
    }

    @keyframes spin {
      0% { transform: translate(-50%, -50%) rotate(0deg); }
      100% { transform: translate(-50%, -50%) rotate(360deg); }
    }

    @media (min-width: 640px) {
      #cash-5-app-container__close {
        font-size: 14px;
      }
    }
    @media (min-width: 1280px) {
      #cash-5-app-container__close {
        font-size: 18px;
      }
    }
  `));

  const head = document.head || document.getElementsByTagName('head')[0];
  head.appendChild(style);

  /**
   * Create our application container appended to the body.
   */
  const loader = document.createElement('div');
  loader.classList.add('cash-5-app-loader');

  const vueApp = document.createElement('div');
  vueApp.setAttribute('id', 'cash-5-app');
  vueApp.appendChild(loader);

  container.setAttribute('id', 'cash-5-app-container');
  container.classList.add('cash-5-app-container--hide');
  container.appendChild(vueApp);

  document.body.appendChild(container);

  return vueApp;
}
